import { useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { trpc, type RouterOutputs } from "@/trpc";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import TagChip from "./TagChip";
import TagColorSwatches from "./TagColorSwatches";
import type { LeadTagColor } from "../../../../agent/src/core/leadTag/types";
import {
  LEAD_TAG_NAME_MAX_LENGTH,
  normalizeLeadTagName,
} from "../../../../agent/src/helpers/normalizeLeadTagName";

type LeadDetail = RouterOutputs["leads"]["get"];
type LeadTag = RouterOutputs["leadTags"]["list"][number];

const LeadTagsEditor = ({ lead, merchantId }: { lead: LeadDetail; merchantId: string }) => {
  const isMobile = useIsMobile();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);

  const tags = trpc.leadTags.list.useQuery(
    { merchantId },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );

  const tagIds = lead.tagIds ?? [];
  const leadTags = (tags.data ?? []).filter((tag) => tagIds.includes(tag.id));

  const handleTagIdsChange = (result: { tagIds: string[] }) => {
    utils.leads.get.setData({ merchantId, id: lead.id }, (prev) =>
      prev ? { ...prev, tagIds: result.tagIds } : prev
    );
    utils.leads.listActiveByPosition.invalidate();
    utils.leads.searchActiveNonRealtime.invalidate();
    utils.leads.listInactiveByProgress.invalidate();
    utils.leads.searchInactive.invalidate();
    utils.inbox.list.invalidate();
  };
  const handleError = (fallback: string) => (error: { message?: string }) =>
    toast.error(error.message || fallback);

  const addToLead = trpc.leadTags.addToLead.useMutation({
    onSuccess: handleTagIdsChange,
    onError: handleError("No se pudo agregar la etiqueta"),
  });

  const removeFromLead = trpc.leadTags.removeFromLead.useMutation({
    onSuccess: handleTagIdsChange,
    onError: handleError("No se pudo quitar la etiqueta"),
  });

  const createTag = trpc.leadTags.create.useMutation({
    onSuccess: (tag) => {
      utils.leadTags.list.invalidate({ merchantId });
      toast.success("Etiqueta creada");
      addToLead.mutate({ merchantId, leadId: lead.id, tagId: tag.id });
    },
    onError: handleError("No se pudo crear la etiqueta"),
  });

  const isPending = addToLead.isLoading || removeFromLead.isLoading || createTag.isLoading;

  const handleToggle = (tagId: string) => {
    if (isPending) return;
    const mutation = tagIds.includes(tagId) ? removeFromLead : addToLead;
    mutation.mutate({ merchantId, leadId: lead.id, tagId });
  };

  const handleCreate = (data: { name: string; color: LeadTagColor }) => {
    if (isPending) return;
    createTag.mutate({ merchantId, ...data });
  };

  const trigger = (
    <Button
      variant="outline"
      size="sm"
      className="min-h-11"
      onClick={isMobile ? () => setOpen(true) : undefined}
    >
      <Plus className="mr-1.5 size-4" />
      Agregar
    </Button>
  );

  const picker = (
    <LeadTagPicker
      tags={tags.data ?? []}
      isLoading={tags.isLoading}
      tagIds={tagIds}
      isPending={isPending}
      onToggle={handleToggle}
      onCreate={handleCreate}
    />
  );

  return (
    <div className="flex flex-wrap items-center gap-x-1.5">
      {leadTags.map((tag) => (
        <TagChip
          key={tag.id}
          name={tag.name}
          color={tag.color}
          archived={tag.status === "archived"}
          onRemove={() => handleToggle(tag.id)}
        />
      ))}
      {isMobile ? (
        <>
          {trigger}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent side="bottom" className="h-[70vh] p-4">
              <SheetHeader>
                <SheetTitle className="text-left text-base">Etiquetas</SheetTitle>
              </SheetHeader>
              <div className="mt-3">{picker}</div>
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent align="start" className="w-[280px] p-3">
            {picker}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

const LeadTagPicker = ({
  tags,
  isLoading,
  tagIds,
  isPending,
  onToggle,
  onCreate,
}: {
  tags: LeadTag[];
  isLoading: boolean;
  tagIds: string[];
  isPending: boolean;
  onToggle: (tagId: string) => void;
  onCreate: (data: { name: string; color: LeadTagColor }) => void;
}) => {
  const [search, setSearch] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [color, setColor] = useState<LeadTagColor>("purple");

  const needle = normalizeLeadTagName(search);
  const activeTags = tags.filter((tag) => tag.status === "active");
  const visibleTags = activeTags.filter((tag) => normalizeLeadTagName(tag.name).includes(needle));
  const hasExactMatch = activeTags.some((tag) => normalizeLeadTagName(tag.name) === needle);
  const canCreate = !!needle && !hasExactMatch;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setIsCreating(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
        <Input
          autoFocus
          value={search}
          maxLength={LEAD_TAG_NAME_MAX_LENGTH}
          onChange={(event) => handleSearchChange(event.target.value)}
          placeholder="Buscar o crear etiqueta…"
          className="h-9 pl-9"
          aria-label="Buscar o crear etiqueta"
        />
      </div>

      <div className="max-h-[320px] overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-1.5 py-1">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <>
            {visibleTags.map((tag) => (
              <label
                key={tag.id}
                className={cn(
                  "flex min-h-11 items-center gap-3 rounded-md px-2",
                  isPending ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-gray-50"
                )}
              >
                <Checkbox
                  checked={tagIds.includes(tag.id)}
                  disabled={isPending}
                  onCheckedChange={() => onToggle(tag.id)}
                />
                <TagChip name={tag.name} color={tag.color} />
              </label>
            ))}
            {visibleTags.length === 0 && !needle ? (
              <p className="py-6 text-center text-sm text-gray-500">
                Aún no hay etiquetas. Escribe un nombre para crear la primera.
              </p>
            ) : null}
          </>
        )}
      </div>

      {canCreate && !isCreating ? (
        <Button
          variant="ghost"
          className="min-h-11 justify-start"
          disabled={isPending}
          onClick={() => setIsCreating(true)}
        >
          <Plus className="mr-2 size-4 shrink-0" />
          <span className="truncate">Crear etiqueta “{search.trim()}”</span>
        </Button>
      ) : null}

      {canCreate && isCreating ? (
        <div className="flex flex-col gap-3 border-t border-gray-200 pt-3">
          <Label id="lead-tag-picker-color">Color de “{search.trim()}”</Label>
          <TagColorSwatches
            value={color}
            onChange={setColor}
            labelledBy="lead-tag-picker-color"
            className="md:grid-cols-4 md:gap-3"
          />
          <Button disabled={isPending} onClick={() => onCreate({ name: search.trim(), color })}>
            Crear etiqueta
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default LeadTagsEditor;
